import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { errMsg, api } from "@/lib/api";
import { Copy, Download, KeyRound } from "lucide-react";

export function CodeInput({ value, onChange, testId, length = 6, placeholder }) {
  return (
    <Input inputMode="numeric" autoComplete="one-time-code" maxLength={length === 6 ? 6 : 12} value={value}
      onChange={(e) => onChange(length === 6 ? e.target.value.replace(/\D/g, "") : e.target.value.toUpperCase())}
      placeholder={placeholder || "6-digit code"} data-testid={testId}
      className="font-mono text-lg tracking-[0.4em] text-center h-12" />
  );
}

/** Authenticator enrollment: start() returns {qr_code_data_url, secret}; confirm(code) resolves on success. */
export function TotpSetup({ start, confirm, onCancel }) {
  const [prov, setProv] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  if (!prov) {
    return (
      <div className="space-y-4" data-testid="totp-setup-intro">
        <p className="text-sm text-slate-600">Use Google Authenticator or any compatible TOTP app. You will scan a QR code and enter the 6-digit code it shows.</p>
        <div className="flex gap-2">
          <Button onClick={() => run(async () => setProv(await start()))} disabled={busy} data-testid="totp-begin-button">Generate QR code</Button>
          {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="totp-setup-panel">
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <img src={prov.qr_code_data_url} alt="Authenticator QR code" className="w-44 h-44 border border-slate-200 rounded-md bg-white" data-testid="totp-qr-image" />
        <div className="space-y-2 text-sm min-w-0">
          <p className="font-medium text-slate-900">1. Scan the QR code with your authenticator app</p>
          <p className="text-slate-500">Can't scan? Enter this key manually (time-based):</p>
          <code className="block break-all bg-slate-100 rounded px-2 py-1 text-amber-800" data-testid="totp-manual-secret">{prov.secret}</code>
          <p className="font-medium text-slate-900 pt-2">2. Enter the current 6-digit code</p>
        </div>
      </div>
      <CodeInput value={code} onChange={setCode} testId="totp-code-input" />
      <div className="flex gap-2">
        <Button disabled={busy || code.length !== 6} onClick={() => run(() => confirm(code))} data-testid="totp-confirm-button">Verify & activate</Button>
        {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
}

/** Email OTP enrollment. start() -> {step}; confirm(step, code) -> result with email_otp_enabled */
export function EmailEnroll({ start, confirm, onCancel, email }) {
  const [step, setStep] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await start(); setStep(r.step); setCode(""); toast.success(r.message || "Code sent"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await confirm(step, code);
      if (r && r.email_otp_enabled === false) { toast.success("Email verified. Now confirm Email OTP enrollment."); setStep(null); setCode(""); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="email-enroll-panel">
      <p className="text-sm text-slate-600">A one-time code will be sent to <span className="font-medium text-slate-900">{email}</span>.
        {step === "verify_email" ? " This first code verifies your email address only — it does not enable Email OTP yet." : ""}</p>
      {!step ? (
        <div className="flex gap-2">
          <Button onClick={send} disabled={busy} data-testid="email-enroll-send-button">Send code</Button>
          {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
        </div>
      ) : (
        <>
          <p className="text-xs uppercase tracking-wider font-semibold text-amber-700" data-testid="email-enroll-step">
            {step === "verify_email" ? "Step 1 · Verify email address" : "Confirm Email OTP enrollment"}</p>
          <CodeInput value={code} onChange={setCode} testId="email-enroll-code-input" />
          <div className="flex gap-2">
            <Button disabled={busy || code.length !== 6} onClick={submit} data-testid="email-enroll-confirm-button">Verify</Button>
            <Button variant="outline" disabled={busy} onClick={send} data-testid="email-enroll-resend-button">Resend</Button>
          </div>
        </>
      )}
    </div>
  );
}

export function RecoveryCodesDialog({ codes, onClose }) {
  const text = (codes || []).join("\n");
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([`CASURECO II recovery codes\n\n${text}\n`], { type: "text/plain" }));
    a.download = "casureco2-recovery-codes.txt";
    a.click();
  };
  return (
    <Dialog open={!!codes?.length} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="recovery-codes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-amber-600" /> Save your recovery codes</DialogTitle>
          <DialogDescription>Each code works once if you lose access to your email or authenticator. They will not be shown again.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="recovery-codes-list">
          {(codes || []).map((c) => <span key={c}>{c}</span>)}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(text); toast.success("Copied"); }}><Copy className="h-4 w-4 mr-1" />Copy</Button>
          <Button variant="outline" onClick={download}><Download className="h-4 w-4 mr-1" />Download</Button>
          <Button onClick={onClose} data-testid="recovery-codes-done-button">I've saved them</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Prompts for a current second factor (OTP / authenticator / recovery code). */
export function FactorDialog({ open, title, description, methods, onSubmit, onClose, confirmLabel = "Confirm" }) {
  const [method, setMethod] = useState(methods?.[0] || "totp");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const sendEmail = async () => {
    try { await api.post("/me/2fa/step-up/email"); toast.success("Code sent to your email"); } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async () => {
    setBusy(true);
    try { await onSubmit(method, code); setCode(""); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const opts = [...(methods || []), "recovery"];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="factor-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Label>Verify with</Label>
          <div className="flex gap-2 flex-wrap">
            {opts.map((m) => (
              <Button key={m} type="button" size="sm" variant={method === m ? "default" : "outline"} onClick={() => { setMethod(m); setCode(""); }}
                data-testid={`factor-method-${m}`}>{m === "email" ? "Email OTP" : m === "totp" ? "Authenticator" : "Recovery code"}</Button>
            ))}
          </div>
          {method === "email" && <Button type="button" variant="secondary" size="sm" onClick={sendEmail} data-testid="factor-send-email-button">Send code to my email</Button>}
          <CodeInput value={code} onChange={setCode} length={method === "recovery" ? 9 : 6} placeholder={method === "recovery" ? "XXXX-XXXX" : undefined} testId="factor-code-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || code.length < 6} data-testid="factor-submit-button">{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
